import type { SagaIterator } from "redux-saga";
import { call, fork, put, select, take, takeLeading } from "redux-saga/effects";
import type { ActionType } from "typesafe-actions";
import { getType } from "typesafe-actions";

import * as orderHistoryApi from "@/shop/api/personalized/orderHistory";
import type { ILatestOrderDocument, ILatestQuoteDocument, IOrderHistoryDocument } from "@/shop/types/personalized/orderHistory";

import { orderHistoryDetailsStateSelector } from "../selectors";
import * as OrderHistoryActions from "./actions";
import type { IOrderHistoryDetailsState } from "./types";

function* fetchOrderHistoryDocument(action: ActionType<typeof OrderHistoryActions.fetchOrderHistoryDocument>): SagaIterator {
  try {
    const { query } = action.payload;
    const state = (yield select(orderHistoryDetailsStateSelector)) as IOrderHistoryDetailsState;

    // already in storage
    if (state.docId === query.docId && state.docType === query.docType && !state.requiresUpdate) {
      return;
    }

    yield put(OrderHistoryActions.fetchOrderHistoryDocumentStart(query));
    const doc = (yield call(orderHistoryApi.fetchOrderHistoryDocument, query)) as IOrderHistoryDocument; // is arr? // docType: string, docSrc: string, docId: string
    yield put(OrderHistoryActions.fetchOrderHistoryDocumentSuccess(doc));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(OrderHistoryActions.fetchOrderHistoryFail(err));
  }
}

function* fetchLatestOrderHistory(_: ActionType<typeof OrderHistoryActions.fetchLatestOrderHistory>): SagaIterator {
  try {
    yield put(OrderHistoryActions.fetchLatestOrderHistoryStart());
    const data = (yield call(orderHistoryApi.fetchLatestOrdersAndQuotes)) as {
      orders: ILatestOrderDocument[];
      quotes: ILatestQuoteDocument[];
      numOrders: number;
      numQuotes: number;
    };
    yield put(OrderHistoryActions.fetchLatestOrderHistorySuccess(data));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(OrderHistoryActions.fetchLatestOrderHistoryFail(err));
  }
}

function* fetchLatestOrderHistoryOnceSaga(): SagaIterator {
  const params = (yield take(getType(OrderHistoryActions.fetchLatestOrderHistory))) as ActionType<typeof OrderHistoryActions.fetchLatestOrderHistory>;
  yield fork(fetchLatestOrderHistory, params);
}

export default function* orderHistorySaga(): SagaIterator {
  yield takeLeading(OrderHistoryActions.fetchOrderHistoryDocument, fetchOrderHistoryDocument);
  yield fork(fetchLatestOrderHistoryOnceSaga);
}
